import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, FileBadge2, MapPin, X, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./components/AuthLayout";
import { AuthService } from "../../services/auth.service";
import { getAuthUser, updateAuthUser } from "./components/authStore";
import { locationService } from "../../services/location.service";

const MAX_FILE_SIZE_MB = 5;
const DOC_ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
const PHOTO_ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const onlyDigits = (value, max = 10) =>
    String(value || "")
        .replace(/\D/g, "")
        .slice(0, max);

const cleanText = (value) => String(value || "").trim();

const isValidPhone = (value) => /^[6-9]\d{9}$/.test(cleanText(value));
const isValidPincode = (value) => /^\d{6}$/.test(cleanText(value));

const isValidNameText = (value) => {
    const text = cleanText(value);
    return text.length >= 2 && /^[A-Za-z\s.'-]+$/.test(text);
};

const isValidRelationText = (value) => {
    const text = cleanText(value);
    return text.length >= 2 && /^[A-Za-z\s.'-]+$/.test(text);
};

const isValidLocationText = (value) => {
    const text = cleanText(value);
    return text.length >= 2 && /^[A-Za-z\s.'-]+$/.test(text);
};

const isValidCountryText = (value) => {
    const text = cleanText(value);
    return text.length >= 2 && /^[A-Za-z\s.'-]+$/.test(text);
};

const isAllowedFileType = (file, allowedTypes) => {
    if (!file) return true;
    return allowedTypes.includes(String(file.type || "").toLowerCase());
};

const isValidFileSize = (file, maxMb = MAX_FILE_SIZE_MB) => {
    if (!file) return true;
    return Number(file.size || 0) <= maxMb * 1024 * 1024;
};

const isSameFile = (fileA, fileB) => {
    if (!fileA || !fileB) return false;
    return (
        fileA.name === fileB.name &&
        fileA.size === fileB.size &&
        fileA.lastModified === fileB.lastModified
    );
};

function Label({ children }) {
    return (
        <label className="mb-1 block text-[12px] font-semibold tracking-wider text-slate-500">
            {children}
        </label>
    );
}

function Input(props) {
    return (
        <input
            {...props}
            className={
                "h-[44px] w-full rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[13px] text-slate-700 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] " +
                (props.className || "")
            }
        />
    );
}

function Textarea(props) {
    return (
        <textarea
            {...props}
            className={
                "w-full rounded-[10px] border border-[#f2c6cc] bg-white px-4 py-3 text-[13px] text-slate-700 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] " +
                (props.className || "")
            }
        />
    );
}

function FileInput({ onChange, accept }) {
    return (
        <input
            type="file"
            accept={accept}
            onChange={onChange}
            className="block h-[44px] w-full rounded-[10px] border border-[#f2c6cc] bg-white px-3 py-[10px] text-[13px] text-slate-700 file:mr-3 file:rounded-[8px] file:border-0 file:bg-[#fff1f3] file:px-3 file:py-2 file:text-[12px] file:font-semibold file:text-[#d86a78] focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
        />
    );
}

function FileStatus({ file, existing }) {
    if (file) {
        return (
            <div className="mt-2 text-[12px] text-emerald-600">
                Selected: {file.name}
            </div>
        );
    }

    if (existing?.url) {
        return (
            <div className="mt-2 break-all text-[12px] text-slate-500">
                Already uploaded: {existing.originalName || existing.url}
            </div>
        );
    }

    return <div className="mt-2 text-[12px] text-slate-400">No file selected</div>;
}

function StepPill({ active, done, number, title }) {
    return (
        <div
            className={`rounded-[14px] border px-3 py-3 ${
                active
                    ? "border-[#d86a78] bg-[#fff5f6]"
                    : done
                    ? "border-[#d7f0cf] bg-[#f5fff2]"
                    : "border-slate-200 bg-white"
            }`}
        >
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                Step {number}
            </div>
            <div className="mt-1 text-[13px] font-semibold text-slate-700">{title}</div>
        </div>
    );
}

function WaitingApprovalModal({ open, onClose }) {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-[440px] rounded-[24px] bg-white p-6 text-center shadow-2xl sm:p-8">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#fff3f5] text-[#d86a78]">
                    <CheckCircle2 size={34} />
                </div>

                <h2 className="mt-5 text-[28px] font-bold tracking-wide text-[#d86a78]">
                    REQUEST SENT
                </h2>

                <p className="mt-3 text-[14px] leading-6 text-slate-500">
                    Your onboarding is complete and approval request has been sent to admin.
                    <br />
                    Please wait until admin approves your account.
                </p>

                <button
                    type="button"
                    onClick={onClose}
                    className="mt-6 h-[44px] w-full rounded-[10px] bg-[#d86a78] text-[13px] font-semibold text-white hover:bg-[#c95b69]"
                >
                    OKAY
                </button>
            </div>
        </div>
    );
}

function RejectedApprovalModal({ open, note, onEditAgain }) {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-[460px] rounded-[24px] bg-white p-6 text-center shadow-2xl sm:p-8">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]">
                    <XCircle size={34} />
                </div>

                <h2 className="mt-5 text-[28px] font-bold tracking-wide text-[#d86a78]">
                    APPROVAL REJECTED
                </h2>

                <p className="mt-3 text-[14px] leading-6 text-slate-500">
                    Admin rejected your previous onboarding request.
                </p>

                <div className="mt-5 rounded-[16px] border border-[#f6d8dd] bg-[#fff8f9] px-4 py-4 text-left">
                    <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                        Admin Note
                    </div>
                    <div className="mt-2 text-[14px] text-slate-700">
                        {note || "No note provided by admin."}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={onEditAgain}
                    className="mt-6 h-[44px] w-full rounded-[10px] bg-[#d86a78] text-[13px] font-semibold text-white hover:bg-[#c95b69]"
                >
                    ONBOARDING AGAIN
                </button>
            </div>
        </div>
    );
}

function AppPopup({ open, type = "error", title, message, onClose }) {
    if (!open) return null;

    const isSuccess = type === "success";
    const Icon = isSuccess ? CheckCircle2 : AlertCircle;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
            <div className="relative w-full max-w-[430px] rounded-[24px] border border-[#f6d8dd] bg-white p-6 shadow-[0_20px_70px_rgba(216,106,120,0.18)] sm:p-7">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-4 top-4 rounded-full p-1 text-slate-400 transition hover:bg-[#fff3f5] hover:text-[#d86a78]"
                >
                    <X size={18} />
                </button>

                <div
                    className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${
                        isSuccess ? "bg-[#eefbf3] text-emerald-600" : "bg-[#fff3f5] text-[#d86a78]"
                    }`}
                >
                    <Icon size={32} />
                </div>

                <h3 className="mt-5 text-center text-[24px] font-bold tracking-wide text-[#d86a78]">
                    {title}
                </h3>

                <p className="mt-3 text-center text-[14px] leading-6 text-slate-500">
                    {message}
                </p>

                <button
                    type="button"
                    onClick={onClose}
                    className="mt-6 h-[44px] w-full rounded-[10px] bg-[#d86a78] text-[13px] font-semibold text-white hover:bg-[#c95b69]"
                >
                    OKAY
                </button>
            </div>
        </div>
    );
}

function CityDropdown({ options, value, onSelect, visible }) {
    if (!visible || !options.length) return null;

    return (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-56 overflow-auto rounded-[14px] border border-[#f2c6cc] bg-white shadow-lg">
            {options.map((city) => (
                <button
                    key={city}
                    type="button"
                    onClick={() => onSelect(city)}
                    className={`flex w-full items-center gap-2 px-4 py-3 text-left text-[13px] text-slate-700 transition hover:bg-[#fff5f6] ${
                        String(value).trim().toLowerCase() === String(city).trim().toLowerCase()
                            ? "bg-[#fff5f6] text-[#d86a78]"
                            : ""
                    }`}
                >
                    <MapPin size={14} className="text-[#d86a78]" />
                    <span>{city}</span>
                </button>
            ))}
        </div>
    );
}

export default function EmployeeOnboarding() {
    const nav = useNavigate();
    const authUser = getAuthUser();

    const onboardingData = authUser?.onboarding || {};
    const savedAddress = onboardingData?.address || {};
    const savedDocuments = onboardingData?.documents || {};
    const savedPhotos = onboardingData?.photos || {};

    const [cityOptions, setCityOptions] = useState([]);
    const [cityLoading, setCityLoading] = useState(false);
    const [pincodeLoading, setPincodeLoading] = useState(false);
    const [showCityDropdown, setShowCityDropdown] = useState(false);

    const [popup, setPopup] = useState({
        open: false,
        type: "error",
        title: "",
        message: "",
    });

    const openPopup = ({ type = "error", title, message }) => {
        setPopup({ open: true, type, title, message });
    };

    const closePopup = () => {
        setPopup((prev) => ({ ...prev, open: false }));
    };

    const existingDocs = {
        aadhaar:
            savedDocuments?.aadhaar ||
            (savedDocuments?.aadhaarUrl ? { url: savedDocuments.aadhaarUrl } : null),
        pan: savedDocuments?.pan || (savedDocuments?.panUrl ? { url: savedDocuments.panUrl } : null),
        photo1:
            savedPhotos?.photo1 ||
            savedDocuments?.photo1 ||
            (savedPhotos?.photo1Url || savedDocuments?.photo1Url
                ? { url: savedPhotos?.photo1Url || savedDocuments?.photo1Url }
                : null),
        photo2:
            savedPhotos?.photo2 ||
            savedDocuments?.photo2 ||
            (savedPhotos?.photo2Url || savedDocuments?.photo2Url
                ? { url: savedPhotos?.photo2Url || savedDocuments?.photo2Url }
                : null),
    };

    const [currentStep, setCurrentStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [showWaiting, setShowWaiting] = useState(false);
    const [showRejected, setShowRejected] = useState(
        String(authUser?.approvalStatus || authUser?.approval_status || "").toUpperCase() ===
            "REJECTED"
    );

    const [form, setForm] = useState({
        emergencyName: onboardingData?.emergencyContact?.name || "",
        emergencyPhone: onboardingData?.emergencyContact?.phone || "",
        emergencyRelation: onboardingData?.emergencyContact?.relation || "",

        excelKnowledgeConfirmed: Boolean(onboardingData?.excelKnowledgeConfirmed),

        ref1Name: onboardingData?.referenceContacts?.[0]?.name || "",
        ref1Phone: onboardingData?.referenceContacts?.[0]?.phone || "",
        ref1Relation: onboardingData?.referenceContacts?.[0]?.relation || "",

        ref2Name: onboardingData?.referenceContacts?.[1]?.name || "",
        ref2Phone: onboardingData?.referenceContacts?.[1]?.phone || "",
        ref2Relation: onboardingData?.referenceContacts?.[1]?.relation || "",

        aadhaarFile: null,
        panFile: null,
        photo1File: null,
        photo2File: null,

        addressLine1: savedAddress?.line1 || "",
        city: savedAddress?.city || "",
        state: savedAddress?.state || "",
        country: savedAddress?.country || "IN",
        pincode: savedAddress?.pincode || "",
        notes: onboardingData?.notes || "",
    });

    useEffect(() => {
        const step = Number(authUser?.onboardingStep || 1);
        if (step >= 1 && step <= 5) {
            setCurrentStep(step);
        }
    }, [authUser?.onboardingStep]);

    const set = (key) => (e) => {
        const target = e?.target;
        const value =
            target?.type === "checkbox"
                ? target.checked
                : target?.type === "file"
                ? target.files?.[0] || null
                : target.value;

        setForm((prev) => ({ ...prev, [key]: value }));

        if (key === "city") {
            setShowCityDropdown(true);
        }
    };

    const referenceContacts = useMemo(() => {
        const refs = [];

        const ref1Filled = form.ref1Name || form.ref1Phone || form.ref1Relation;
        const ref2Filled = form.ref2Name || form.ref2Phone || form.ref2Relation;

        if (ref1Filled) {
            refs.push({
                name: form.ref1Name.trim(),
                phone: form.ref1Phone.trim(),
                relation: form.ref1Relation.trim(),
            });
        }

        if (ref2Filled) {
            refs.push({
                name: form.ref2Name.trim(),
                phone: form.ref2Phone.trim(),
                relation: form.ref2Relation.trim(),
            });
        }

        return refs;
    }, [form]);

    const payload = useMemo(
        () => ({
            onboardingStep: 5,
            isOnboardingComplete: true,
            emergencyContact: {
                name: form.emergencyName.trim(),
                phone: form.emergencyPhone.trim(),
                relation: form.emergencyRelation.trim(),
            },
            excelKnowledgeConfirmed: Boolean(form.excelKnowledgeConfirmed),
            referenceContacts,
            onboarding: {
                emergencyContact: {
                    name: form.emergencyName.trim(),
                    phone: form.emergencyPhone.trim(),
                    relation: form.emergencyRelation.trim(),
                },
                excelKnowledgeConfirmed: Boolean(form.excelKnowledgeConfirmed),
                referenceContacts,
                address: {
                    line1: form.addressLine1.trim(),
                    city: form.city.trim(),
                    state: form.state.trim(),
                    country: form.country.trim(),
                    pincode: form.pincode.trim(),
                },
                notes: form.notes.trim(),
            },
        }),
        [form, referenceContacts]
    );

    const stepTitles = [
        "Emergency Contact",
        "Excel Knowledge",
        "Reference Contacts",
        "Documents",
        "Address & Notes",
    ];

    useEffect(() => {
        const stateName = String(form.state || "").trim();
        const countryName =
            String(form.country || "").trim().toUpperCase() === "IN" ? "India" : form.country;

        if (!stateName) {
            setCityOptions([]);
            return;
        }

        let active = true;

        (async () => {
            try {
                setCityLoading(true);

                const rows = await locationService.searchCities({ countryName, stateName });
                if (!active) return;

                const names = rows
                    .map((x) => x?.name || x?.city_name || x?.city || "")
                    .filter(Boolean);

                setCityOptions([...new Set(names)]);
            } catch {
                if (active) setCityOptions([]);
            } finally {
                if (active) setCityLoading(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [form.state, form.country]);

    useEffect(() => {
        const pin = String(form.pincode || "").replace(/\D/g, "");

        if (pin.length !== 6) return;

        let active = true;

        (async () => {
            try {
                setPincodeLoading(true);

                const rows = await locationService.getPincodeDetails({
                    pincode: pin,
                    country: String(form.country || "IN").trim() || "IN",
                });

                if (!active) return;

                const first = Array.isArray(rows) ? rows[0] : null;
                if (!first) {
                    openPopup({
                        type: "error",
                        title: "INVALID PINCODE",
                        message: "Pincode details not found. Please enter a valid pincode.",
                    });
                    return;
                }

                const nextCity =
                    first?.city ||
                    first?.district ||
                    first?.division ||
                    first?.city_name ||
                    "";
                const nextState = first?.state || first?.state_name || "";
                const nextCountry = first?.country || first?.country_code || "IN";

                setForm((prev) => ({
                    ...prev,
                    city: prev.city?.trim() ? prev.city : nextCity,
                    state: nextState || prev.state,
                    country: nextCountry || prev.country,
                }));
            } catch (err) {
                if (!active) return;

                openPopup({
                    type: "error",
                    title: "PINCODE ERROR",
                    message: err?.message || "Unable to fetch pincode details.",
                });
            } finally {
                if (active) setPincodeLoading(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [form.pincode]);

    const filteredCityOptions = useMemo(() => {
        const q = String(form.city || "").trim().toLowerCase();
        if (!q) return cityOptions.slice(0, 50);
        return cityOptions
            .filter((city) => city.toLowerCase().includes(q))
            .slice(0, 50);
    }, [cityOptions, form.city]);

    const validateUploadedFile = ({ file, allowedTypes, label, onlyImage = false }) => {
        if (!file) return true;

        if (!isAllowedFileType(file, allowedTypes)) {
            openPopup({
                type: "error",
                title: "INVALID FILE TYPE",
                message: onlyImage
                    ? `${label} must be JPG, PNG, or WEBP image.`
                    : `${label} must be JPG, PNG, WEBP, or PDF file.`,
            });
            return false;
        }

        if (!isValidFileSize(file)) {
            openPopup({
                type: "error",
                title: "FILE TOO LARGE",
                message: `${label} file size must be less than or equal to ${MAX_FILE_SIZE_MB} MB.`,
            });
            return false;
        }

        return true;
    };

    const validateStep = (step) => {
        if (step === 1) {
            if (!form.emergencyName || !form.emergencyPhone || !form.emergencyRelation) {
                openPopup({
                    type: "error",
                    title: "MISSING DETAILS",
                    message: "Please fill emergency contact details.",
                });
                return false;
            }

            if (!isValidNameText(form.emergencyName)) {
                openPopup({
                    type: "error",
                    title: "INVALID NAME",
                    message: "Emergency contact name should contain only letters.",
                });
                return false;
            }

            if (!isValidPhone(form.emergencyPhone)) {
                openPopup({
                    type: "error",
                    title: "INVALID PHONE",
                    message: "Please enter a valid 10-digit emergency contact phone number.",
                });
                return false;
            }

            if (!isValidRelationText(form.emergencyRelation)) {
                openPopup({
                    type: "error",
                    title: "INVALID RELATION",
                    message: "Emergency relation should contain only letters.",
                });
                return false;
            }
        }

        if (step === 2) {
            if (!form.excelKnowledgeConfirmed) {
                openPopup({
                    type: "error",
                    title: "CONFIRM REQUIRED",
                    message: "Please confirm excel knowledge.",
                });
                return false;
            }
        }

        if (step === 3) {
            const ref1Filled = form.ref1Name || form.ref1Phone || form.ref1Relation;
            const ref2Filled = form.ref2Name || form.ref2Phone || form.ref2Relation;

            if (!ref1Filled && !ref2Filled) {
                openPopup({
                    type: "error",
                    title: "REFERENCE REQUIRED",
                    message: "Please fill at least one reference contact.",
                });
                return false;
            }

            if (ref1Filled && (!form.ref1Name || !form.ref1Phone || !form.ref1Relation)) {
                openPopup({
                    type: "error",
                    title: "INCOMPLETE REFERENCE",
                    message: "Please complete Reference Contact 1.",
                });
                return false;
            }

            if (ref2Filled && (!form.ref2Name || !form.ref2Phone || !form.ref2Relation)) {
                openPopup({
                    type: "error",
                    title: "INCOMPLETE REFERENCE",
                    message: "Please complete Reference Contact 2.",
                });
                return false;
            }

            if (ref1Filled) {
                if (!isValidNameText(form.ref1Name)) {
                    openPopup({
                        type: "error",
                        title: "INVALID REFERENCE",
                        message: "Reference Contact 1 name should contain only letters.",
                    });
                    return false;
                }

                if (!isValidPhone(form.ref1Phone)) {
                    openPopup({
                        type: "error",
                        title: "INVALID PHONE",
                        message: "Please enter a valid 10-digit phone number for Reference Contact 1.",
                    });
                    return false;
                }

                if (!isValidRelationText(form.ref1Relation)) {
                    openPopup({
                        type: "error",
                        title: "INVALID RELATION",
                        message: "Reference Contact 1 relation should contain only letters.",
                    });
                    return false;
                }
            }

            if (ref2Filled) {
                if (!isValidNameText(form.ref2Name)) {
                    openPopup({
                        type: "error",
                        title: "INVALID REFERENCE",
                        message: "Reference Contact 2 name should contain only letters.",
                    });
                    return false;
                }

                if (!isValidPhone(form.ref2Phone)) {
                    openPopup({
                        type: "error",
                        title: "INVALID PHONE",
                        message: "Please enter a valid 10-digit phone number for Reference Contact 2.",
                    });
                    return false;
                }

                if (!isValidRelationText(form.ref2Relation)) {
                    openPopup({
                        type: "error",
                        title: "INVALID RELATION",
                        message: "Reference Contact 2 relation should contain only letters.",
                    });
                    return false;
                }
            }
        }

        if (step === 4) {
            const hasAadhaar = !!form.aadhaarFile || !!existingDocs.aadhaar?.url;
            const hasPan = !!form.panFile || !!existingDocs.pan?.url;
            const hasPhoto1 = !!form.photo1File || !!existingDocs.photo1?.url;
            const hasPhoto2 = !!form.photo2File || !!existingDocs.photo2?.url;

            if (!hasAadhaar) {
                openPopup({
                    type: "error",
                    title: "AADHAAR REQUIRED",
                    message: "Please upload Aadhaar file.",
                });
                return false;
            }

            if (!hasPan) {
                openPopup({
                    type: "error",
                    title: "PAN REQUIRED",
                    message: "Please upload PAN file.",
                });
                return false;
            }

            if (!hasPhoto1) {
                openPopup({
                    type: "error",
                    title: "PHOTO 1 REQUIRED",
                    message: "Please upload Photo 1.",
                });
                return false;
            }

            if (!hasPhoto2) {
                openPopup({
                    type: "error",
                    title: "PHOTO 2 REQUIRED",
                    message: "Please upload Photo 2.",
                });
                return false;
            }

            if (
                !validateUploadedFile({
                    file: form.aadhaarFile,
                    allowedTypes: DOC_ALLOWED_TYPES,
                    label: "Aadhaar",
                })
            ) {
                return false;
            }

            if (
                !validateUploadedFile({
                    file: form.panFile,
                    allowedTypes: DOC_ALLOWED_TYPES,
                    label: "PAN",
                })
            ) {
                return false;
            }

            if (
                !validateUploadedFile({
                    file: form.photo1File,
                    allowedTypes: PHOTO_ALLOWED_TYPES,
                    label: "Photo 1",
                    onlyImage: true,
                })
            ) {
                return false;
            }

            if (
                !validateUploadedFile({
                    file: form.photo2File,
                    allowedTypes: PHOTO_ALLOWED_TYPES,
                    label: "Photo 2",
                    onlyImage: true,
                })
            ) {
                return false;
            }

            if (isSameFile(form.photo1File, form.photo2File)) {
                openPopup({
                    type: "error",
                    title: "DUPLICATE PHOTO",
                    message: "Photo 1 and Photo 2 should be different files.",
                });
                return false;
            }
        }

        if (step === 5) {
            if (!form.addressLine1 || !form.city || !form.state || !form.country || !form.pincode) {
                openPopup({
                    type: "error",
                    title: "ADDRESS REQUIRED",
                    message: "Please fill address details.",
                });
                return false;
            }

            if (cleanText(form.addressLine1).length < 5) {
                openPopup({
                    type: "error",
                    title: "INVALID ADDRESS",
                    message: "Please enter a complete address.",
                });
                return false;
            }

            if (!isValidPincode(form.pincode)) {
                openPopup({
                    type: "error",
                    title: "INVALID PINCODE",
                    message: "Please enter a valid 6-digit pincode.",
                });
                return false;
            }

            if (!isValidLocationText(form.city)) {
                openPopup({
                    type: "error",
                    title: "INVALID CITY",
                    message: "City should contain only letters.",
                });
                return false;
            }

            if (!isValidLocationText(form.state)) {
                openPopup({
                    type: "error",
                    title: "INVALID STATE",
                    message: "State should contain only letters.",
                });
                return false;
            }

            if (!isValidCountryText(form.country)) {
                openPopup({
                    type: "error",
                    title: "INVALID COUNTRY",
                    message: "Country should contain only letters.",
                });
                return false;
            }
        }

        return true;
    };

    const validateAllSteps = () => {
        for (let step = 1; step <= 5; step += 1) {
            if (!validateStep(step)) {
                setCurrentStep(step);
                return false;
            }
        }

        return true;
    };

    const nextStep = () => {
        if (!validateStep(currentStep)) return;
        setCurrentStep((s) => Math.min(5, s + 1));
    };

    const prevStep = () => {
        setCurrentStep((s) => Math.max(1, s - 1));
    };

    const buildFormData = () => {
        const fd = new FormData();

        fd.append("onboardingStep", String(payload.onboardingStep));
        fd.append("isOnboardingComplete", String(payload.isOnboardingComplete));
        fd.append("emergencyContact", JSON.stringify(payload.emergencyContact));
        fd.append("excelKnowledgeConfirmed", String(payload.excelKnowledgeConfirmed));
        fd.append("referenceContacts", JSON.stringify(payload.referenceContacts));
        fd.append("onboarding", JSON.stringify(payload.onboarding));

        if (form.aadhaarFile) fd.append("documents.aadhaar", form.aadhaarFile);
        if (form.panFile) fd.append("documents.pan", form.panFile);
        if (form.photo1File) fd.append("documents.photo1", form.photo1File);
        if (form.photo2File) fd.append("documents.photo2", form.photo2File);

        return fd;
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!validateAllSteps()) return;

        try {
            setSaving(true);

            const formData = buildFormData();
            const res = await AuthService.updateOnboarding(formData);

            updateAuthUser(res?.admin || {});
            setShowWaiting(true);
        } catch (err) {
            if (Array.isArray(err?.data?.missing) && err.data.missing.length) {
                openPopup({
                    type: "error",
                    title: "REQUIRED FIELDS MISSING",
                    message: `Please fill required fields: ${err.data.missing.join(", ")}`,
                });
            } else {
                openPopup({
                    type: "error",
                    title: "SUBMISSION FAILED",
                    message: err?.message || "Unable to submit onboarding.",
                });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <AuthLayout>
                <div className="mx-auto w-full max-w-[860px] rounded-[24px] border border-[#f6d8dd] bg-white p-6 shadow-[0_20px_70px_rgba(216,106,120,0.12)] sm:p-8">
                    <div className="mb-6 flex items-center gap-3">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#fff3f5] text-[#d86a78]">
                            <FileBadge2 size={30} />
                        </div>
                        <div>
                            <h1 className="text-[28px] font-bold tracking-wide text-[#d86a78]">
                                EMPLOYEE ONBOARDING
                            </h1>
                            <p className="mt-1 text-[13px] text-slate-500">
                                Complete all 5 onboarding steps to send request for admin approval.
                            </p>
                        </div>
                    </div>

                    <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-5">
                        {stepTitles.map((title, index) => (
                            <StepPill
                                key={title}
                                number={index + 1}
                                title={title}
                                active={currentStep === index + 1}
                                done={currentStep > index + 1}
                            />
                        ))}
                    </div>

                    <form onSubmit={(e) => e.preventDefault()}>
                        {currentStep === 1 ? (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <Label>Emergency Contact Name</Label>
                                    <Input
                                        value={form.emergencyName}
                                        onChange={set("emergencyName")}
                                        placeholder="Raj Sharma"
                                    />
                                </div>
                                <div>
                                    <Label>Emergency Contact Phone</Label>
                                    <Input
                                        value={form.emergencyPhone}
                                        onChange={(e) => {
                                            setForm((prev) => ({
                                                ...prev,
                                                emergencyPhone: onlyDigits(e.target.value, 10),
                                            }));
                                        }}
                                        inputMode="numeric"
                                        maxLength={10}
                                        placeholder="9876543210"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label>Relation</Label>
                                    <Input
                                        value={form.emergencyRelation}
                                        onChange={set("emergencyRelation")}
                                        placeholder="Brother"
                                    />
                                </div>
                            </div>
                        ) : null}

                        {currentStep === 2 ? (
                            <div className="rounded-[18px] border border-[#f2c6cc] bg-[#fff8f9] p-5">
                                <label className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={form.excelKnowledgeConfirmed}
                                        onChange={set("excelKnowledgeConfirmed")}
                                        className="mt-1 h-4 w-4 rounded border-slate-300 text-[#d86a78]"
                                    />
                                    <div>
                                        <div className="text-[15px] font-semibold text-slate-700">
                                            Excel Knowledge Confirmation
                                        </div>
                                        <div className="mt-1 text-[13px] text-slate-500">
                                            Confirm that employee has basic excel knowledge required
                                            for operations.
                                        </div>
                                    </div>
                                </label>
                            </div>
                        ) : null}

                        {currentStep === 3 ? (
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                <div className="rounded-[18px] border border-[#f2c6cc] p-4">
                                    <div className="mb-4 text-[15px] font-bold text-[#d86a78]">
                                        Reference Contact 1
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <Label>Name</Label>
                                            <Input
                                                value={form.ref1Name}
                                                onChange={set("ref1Name")}
                                                placeholder="Pawan Singh"
                                            />
                                        </div>
                                        <div>
                                            <Label>Phone</Label>
                                            <Input
                                                value={form.ref1Phone}
                                                onChange={(e) => {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        ref1Phone: onlyDigits(e.target.value, 10),
                                                    }));
                                                }}
                                                inputMode="numeric"
                                                maxLength={10}
                                                placeholder="9811111111"
                                            />
                                        </div>
                                        <div>
                                            <Label>Relation</Label>
                                            <Input
                                                value={form.ref1Relation}
                                                onChange={set("ref1Relation")}
                                                placeholder="Manager"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-[18px] border border-[#f2c6cc] p-4">
                                    <div className="mb-4 text-[15px] font-bold text-[#d86a78]">
                                        Reference Contact 2
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <Label>Name</Label>
                                            <Input
                                                value={form.ref2Name}
                                                onChange={set("ref2Name")}
                                                placeholder="Neha Sharma"
                                            />
                                        </div>
                                        <div>
                                            <Label>Phone</Label>
                                            <Input
                                                value={form.ref2Phone}
                                                onChange={(e) => {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        ref2Phone: onlyDigits(e.target.value, 10),
                                                    }));
                                                }}
                                                inputMode="numeric"
                                                maxLength={10}
                                                placeholder="9822222222"
                                            />
                                        </div>
                                        <div>
                                            <Label>Relation</Label>
                                            <Input
                                                value={form.ref2Relation}
                                                onChange={set("ref2Relation")}
                                                placeholder="HR"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {currentStep === 4 ? (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <Label>Aadhaar File</Label>
                                    <FileInput
                                        accept=".jpg,.jpeg,.png,.webp,.pdf"
                                        onChange={set("aadhaarFile")}
                                    />
                                    <FileStatus file={form.aadhaarFile} existing={existingDocs.aadhaar} />
                                </div>

                                <div>
                                    <Label>PAN File</Label>
                                    <FileInput
                                        accept=".jpg,.jpeg,.png,.webp,.pdf"
                                        onChange={set("panFile")}
                                    />
                                    <FileStatus file={form.panFile} existing={existingDocs.pan} />
                                </div>

                                <div>
                                    <Label>Photo 1</Label>
                                    <FileInput
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={set("photo1File")}
                                    />
                                    <FileStatus file={form.photo1File} existing={existingDocs.photo1} />
                                </div>

                                <div>
                                    <Label>Photo 2</Label>
                                    <FileInput
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={set("photo2File")}
                                    />
                                    <FileStatus file={form.photo2File} existing={existingDocs.photo2} />
                                </div>
                            </div>
                        ) : null}

                        {currentStep === 5 ? (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div className="md:col-span-2">
                                    <Label>Address Line 1</Label>
                                    <Input
                                        value={form.addressLine1}
                                        onChange={set("addressLine1")}
                                        placeholder="House 44, Sector 18"
                                    />
                                </div>

                                <div>
                                    <Label>Pincode</Label>
                                    <div className="relative">
                                        <Input
                                            value={form.pincode}
                                            onChange={(e) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    pincode: onlyDigits(e.target.value, 6),
                                                }));
                                            }}
                                            inputMode="numeric"
                                            maxLength={6}
                                            placeholder="201301"
                                        />
                                        {pincodeLoading ? (
                                            <div className="mt-2 text-[12px] text-[#d86a78]">
                                                Fetching pincode details...
                                            </div>
                                        ) : null}
                                    </div>
                                </div>

                                <div className="relative">
                                    <Label>City</Label>
                                    <Input
                                        value={form.city}
                                        onChange={set("city")}
                                        onFocus={() => setShowCityDropdown(true)}
                                        onBlur={() => {
                                            setTimeout(() => setShowCityDropdown(false), 150);
                                        }}
                                        placeholder="Noida"
                                    />
                                    {cityLoading ? (
                                        <div className="mt-2 text-[12px] text-[#d86a78]">
                                            Loading city suggestions...
                                        </div>
                                    ) : null}

                                    <CityDropdown
                                        options={filteredCityOptions}
                                        value={form.city}
                                        visible={showCityDropdown && !cityLoading}
                                        onSelect={(city) => {
                                            setForm((prev) => ({ ...prev, city }));
                                            setShowCityDropdown(false);
                                        }}
                                    />
                                </div>

                                <div>
                                    <Label>State</Label>
                                    <Input
                                        value={form.state}
                                        onChange={set("state")}
                                        placeholder="UP"
                                    />
                                </div>

                                <div>
                                    <Label>Country</Label>
                                    <Input
                                        value={form.country}
                                        onChange={set("country")}
                                        placeholder="IN"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <Label>Notes</Label>
                                    <Textarea
                                        rows={4}
                                        value={form.notes}
                                        onChange={set("notes")}
                                        placeholder="All documents uploaded"
                                    />
                                </div>
                            </div>
                        ) : null}

                        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-between">
                            <button
                                type="button"
                                onClick={prevStep}
                                disabled={currentStep === 1}
                                className="h-[44px] rounded-[10px] border border-[#f2c6cc] px-6 text-[13px] font-semibold text-slate-600 disabled:opacity-50"
                            >
                                Previous
                            </button>

                            {currentStep < 5 ? (
                                <button
                                    type="button"
                                    onClick={nextStep}
                                    className="h-[44px] rounded-[10px] bg-[#d86a78] px-6 text-[13px] font-semibold text-white hover:bg-[#c95b69]"
                                >
                                    Next Step
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onSubmit}
                                    disabled={saving}
                                    className="h-[44px] rounded-[10px] bg-[#d86a78] px-6 text-[13px] font-semibold text-white hover:bg-[#c95b69] disabled:opacity-60"
                                >
                                    {saving ? "SUBMITTING..." : "SUBMIT FOR APPROVAL"}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </AuthLayout>

            <WaitingApprovalModal
                open={showWaiting}
                onClose={() => {
                    setShowWaiting(false);
                    nav("/admin/waiting-approval", { replace: true });
                }}
            />

            <RejectedApprovalModal
                open={showRejected}
                note={authUser?.approvalNote}
                onEditAgain={() => {
                    setShowRejected(false);
                    setCurrentStep(1);
                }}
            />

            <AppPopup
                open={popup.open}
                type={popup.type}
                title={popup.title}
                message={popup.message}
                onClose={closePopup}
            />
        </>
    );
}
